import React, { useState } from 'react';
import { Header } from '../../components/Header';
import { Footer } from '../../components/Footer';
import { SalesForm } from '../../components/SalesForm';
import { HardHat, Clipboard, Calendar, Truck, Users, CheckCircle, ArrowRight, Bot } from 'lucide-react';
export function ConstructionSolutionPage() {
  const [isSalesFormOpen, setIsSalesFormOpen] = useState(false);
  return <div className="flex flex-col min-h-screen w-full" data-id="element-899">
      <Header data-id="element-900" />
      <main className="flex-grow" data-id="element-901">
        {/* Hero Section */}
        <div className="bg-black text-white py-16 md:py-24" data-id="element-902">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-903">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center" data-id="element-904">
              <div data-id="element-905">
                <h1 className="text-4xl md:text-5xl font-bold mb-6" data-id="element-906">
                  AI Solutions for Construction
                </h1>
                <p className="text-xl text-gray-300 mb-8" data-id="element-907">
                  Streamline project management, optimize resource allocation,
                  and enhance safety compliance with AI-powered automation.
                </p>
                <button onClick={() => setIsSalesFormOpen(true)} className="bg-white text-black px-6 py-3 rounded-lg font-medium hover:bg-gray-100 transition-colors" data-id="element-908">
                  Request a Demo
                </button>
              </div>
              <div className="relative" data-id="element-909">
                <img src="https://images.unsplash.com/photo-1503387762-592deb58ef4e?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80" alt="Construction site" className="rounded-lg shadow-lg" data-id="element-910" />
                <div className="absolute -bottom-6 -right-6 bg-white p-4 rounded-lg shadow-lg" data-id="element-911">
                  <HardHat className="text-black" size={32} data-id="element-912" />
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Key Benefits */}
        <div className="py-16 md:py-24 bg-white" data-id="element-913">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-914">
            <div className="text-center mb-16" data-id="element-915">
              <h2 className="text-3xl font-bold mb-4" data-id="element-916">
                Key Benefits for Construction Companies
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto" data-id="element-917">
                Our AI agents address the unique challenges faced by
                construction firms, from project planning to execution.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-918">
              <BenefitCard icon={<Calendar size={24} data-id="element-920" />} title="Project Scheduling" description="AI-powered scheduling that adapts to changing conditions, weather forecasts, and resource availability." data-id="element-919" />
              <BenefitCard icon={<Clipboard size={24} data-id="element-922" />} title="Documentation Management" description="Automate the processing of permits, inspection reports, and compliance documentation." data-id="element-921" />
              <BenefitCard icon={<Truck size={24} data-id="element-924" />} title="Supply Chain Optimization" description="Ensure materials arrive just-in-time with intelligent inventory management and supplier coordination." data-id="element-923" />
              <BenefitCard icon={<Users size={24} data-id="element-926" />} title="Workforce Management" description="Optimize crew scheduling, track certifications, and manage subcontractor communications." data-id="element-925" />
              <BenefitCard icon={<CheckCircle size={24} data-id="element-928" />} title="Safety Compliance" description="Automate safety checks, incident reporting, and regulatory compliance documentation." data-id="element-927" />
              <BenefitCard icon={<Bot size={24} data-id="element-930" />} title="Client Communication" description="Keep clients informed with automated progress updates, milestone notifications, and query responses." data-id="element-929" />
            </div>
          </div>
        </div>
        {/* Features */}
        <div className="py-16 md:py-24 bg-white" data-id="element-931">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-932">
            <div className="text-center mb-16" data-id="element-933">
              <h2 className="text-3xl font-bold mb-4" data-id="element-934">
                Key Features for Construction Companies
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto" data-id="element-935">
                Our AI agents offer specialized capabilities designed for the
                unique needs of construction operations.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-936">
              <FeatureCard title="Permit Processing Automation" description="AI agents track permit requirements, prepare documentation, and follow up with authorities to prevent project delays." data-id="element-937" />
              <FeatureCard title="Weather-Adaptive Scheduling" description="Automatically adjust project schedules based on weather forecasts to optimize outdoor work planning." data-id="element-938" />
              <FeatureCard title="Equipment Management" description="Track equipment location, usage, and maintenance schedules to maximize utilization and prevent downtime." data-id="element-939" />
              <FeatureCard title="Subcontractor Coordination" description="Automate communications with subcontractors regarding schedules, deliverables, and compliance requirements." data-id="element-940" />
              <FeatureCard title="Change Order Management" description="Streamline the processing of change orders, including documentation, approvals, and client communications." data-id="element-941" />
              <FeatureCard title="Safety Incident Reporting" description="Automate the documentation, reporting, and follow-up actions for safety incidents and near-misses." data-id="element-942" />
            </div>
          </div>
        </div>
        {/* How It Works */}
        <div className="py-16 md:py-24 bg-gray-50" data-id="element-943">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-944">
            <div className="text-center mb-16" data-id="element-945">
              <h2 className="text-3xl font-bold mb-4" data-id="element-946">How It Works</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto" data-id="element-947">
                Implementing Agentter.ai in your construction operations is
                straightforward and delivers rapid results.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-948">
              <StepCard number="1" title="Connect Your Systems" description="Integrate with your existing project management software, ERP, and documentation platforms." data-id="element-949" />
              <StepCard number="2" title="Configure Your Agents" description="Define the tasks you want to automate using natural language - no coding required." data-id="element-950" />
              <StepCard number="3" title="Deploy and Optimize" description="Launch your AI agents and continuously refine their performance based on project feedback." data-id="element-951" />
            </div>
          </div>
        </div>
        {/* CTA Section */}
        <div className="bg-black text-white py-16" data-id="element-952">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-953">
            <h2 className="text-3xl font-bold mb-6" data-id="element-954">
              Ready to transform your construction operations?
            </h2>
            <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto" data-id="element-955">
              Schedule a personalized demo to see how Agentter.ai can automate
              and optimize your construction workflows.
            </p>
            <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-4" data-id="element-956">
              <button onClick={() => setIsSalesFormOpen(true)} className="bg-white text-black px-6 py-3 rounded-lg font-medium hover:bg-gray-100 transition-colors" data-id="element-957">
                Request a Demo
              </button>
              <a href="#" className="border border-white text-white px-6 py-3 rounded-lg font-medium hover:bg-gray-900 transition-colors" data-id="element-958">
                View Pricing
              </a>
            </div>
          </div>
        </div>
      </main>
      <Footer data-id="element-959" />
      {/* Sales Form Modal */}
      <SalesForm isOpen={isSalesFormOpen} onClose={() => setIsSalesFormOpen(false)} agentType="Construction Solutions" data-id="element-960" />
    </div>;
}
function BenefitCard({
  icon,
  title,
  description
}) {
  return <div className="bg-gray-50 p-6 rounded-lg border border-gray-100 hover:shadow-md transition-shadow" data-id="element-961">
      <div className="bg-black text-white p-3 rounded-lg inline-block mb-4" data-id="element-962">
        {icon}
      </div>
      <h3 className="text-xl font-semibold mb-2" data-id="element-963">{title}</h3>
      <p className="text-gray-600" data-id="element-964">{description}</p>
    </div>;
}
function StepCard({
  number,
  title,
  description
}) {
  return <div className="bg-white p-6 rounded-lg border border-gray-100 hover:shadow-md transition-shadow" data-id="element-965">
      <div className="bg-black text-white w-10 h-10 rounded-full flex items-center justify-center mb-4 font-bold" data-id="element-966">
        {number}
      </div>
      <h3 className="text-xl font-semibold mb-2" data-id="element-967">{title}</h3>
      <p className="text-gray-600" data-id="element-968">{description}</p>
    </div>;
}
function ResultItem({
  text
}) {
  return <div className="flex items-start" data-id="element-969">
      <CheckCircle className="text-green-500 mr-2 mt-1 flex-shrink-0" size={20} data-id="element-970" />
      <span data-id="element-971">{text}</span>
    </div>;
}
function FeatureCard({
  title,
  description
}) {
  return <div className="bg-white p-6 rounded-lg border border-gray-100 hover:shadow-md transition-shadow" data-id="element-972">
      <h3 className="text-xl font-semibold mb-3" data-id="element-973">{title}</h3>
      <p className="text-gray-600" data-id="element-974">{description}</p>
    </div>;
}